import React from 'react';
import { PencilIcon, ShieldCheckIcon } from 'lucide-react';
import { profile } from '../../data/profil';
import type { FamilyMember } from '../../data/carte';
import { familyMembers, memberValues } from '../../data/carte';
const labelClass = 'block text-sm font-semibold text-slate-600';
const fieldClass = 'mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] text-ville-navy transition-colors duration-150 ease-ville focus:border-ville-indigo focus:outline-none focus:ring-2 focus:ring-ville-indigo/20';
const readOnlyClass = 'mt-1 text-[17px] font-bold text-ville-navy';
interface IdentityInfoProps {
  /** Le citoyen détient une carte citoyenne : l'identité est vérifiée et verrouillée. */
  hasCard: boolean;
  /** Ouvre la demande de modification de la carte. */
  onEditCard: (member: FamilyMember) => void;
}

/** Renseignements d'identité, synchronisés avec la carte citoyenne. */
export function IdentityInfo({
  hasCard,
  onEditCard
}: IdentityInfoProps) {
  const holder = familyMembers.find(member => member.id === 'charlotte') ?? familyMembers[0];
  const values = memberValues.charlotte ?? {};
  const identity = [{
    id: 'prenom',
    label: 'Prénom',
    value: profile.firstName,
    type: 'text'
  }, {
    id: 'nom',
    label: 'Nom de famille',
    value: profile.lastName,
    type: 'text'
  }, {
    id: 'genre',
    label: 'Genre',
    value: values.genre ?? '',
    type: 'text'
  }, {
    id: 'naissance',
    label: 'Date de naissance',
    value: profile.birthDate,
    type: 'date'
  }];
  return <section aria-labelledby="identite-titre" className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-id="element-2832" data-mp="2eedq3qefbdb6">
      <h2 id="identite-titre" className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2833" data-mp="29g2epb8hcuqu">
        Mon identité
      </h2>

      <div className="mt-6 flex flex-wrap items-center gap-5" data-id="element-2834" data-mp="24emvlopv4yb3">
        <img src={holder.photo} alt={`Photo de ${profile.firstName} ${profile.lastName}`} className="h-20 w-20 rounded-full object-cover" data-id="element-2835" data-mp="1zgv3oup7xc6w" />

        {hasCard ? <div className="min-w-0" data-id="element-2836" data-mp="1kt4jrxs29064">
            <p className="inline-flex items-center gap-2 text-lg font-extrabold text-emerald-700" data-id="element-2837" data-mp="1ptr538tp5hfv">
              <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-2838" data-mp="1btzlmskev8uh" />
              Identité vérifiée
            </p>
            <p className="text-sm text-slate-600" data-id="element-2839" data-mp="1gskkoonxfmhc">Votre identité est associée à votre carte citoyenne.</p>
            <p className="mt-1.5" data-id="element-2840" data-mp="24iexj688xb1l">
              <button type="button" onClick={() => onEditCard(holder)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-ville-deep underline underline-offset-4 transition-colors duration-150 ease-ville hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2841" data-mp="24j6twj9o3csx">
                <PencilIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2842" data-mp="2ngr9ezqjkz0v" />
                Modifier mon identité
              </button>
            </p>
          </div> : <div className="min-w-0" data-id="element-2843" data-mp="31m9owb1uou76">
            <p className="text-lg font-extrabold text-ville-navy" data-id="element-2844" data-mp="31l8ca256rznc">Vérifiez votre identité</p>
            <p className="text-sm text-slate-600" data-id="element-2845" data-mp="2xky5wodhf77y">
              Obtenez votre carte citoyenne et accédez aux services de votre municipalité.
            </p>
            <button type="button" onClick={() => onEditCard(holder)} className="mt-3 inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-base font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-2846" data-mp="36d9lff725a95">
              <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-2847" data-mp="2nn0iu47czggx" />
              Vérifier mon identité
            </button>
          </div>}
      </div>

      <div className="mt-8 grid gap-5 sm:grid-cols-2" data-id="element-2848" data-mp="7xyt27bbq1ft">
        {identity.map(field => hasCard ? <div key={field.id} data-id="element-2849" data-mp="28llbi7ybdmoy">
              <span className={labelClass} data-id="element-2850" data-mp="28mmynasltzn1">{field.label}</span>
              <p className={readOnlyClass} data-id="element-2851" data-mp="2dk6a62xx3yba">{formatValue(field.id, field.value)}</p>
            </div> : <div key={field.id} data-id="element-2852" data-mp="2ij9hxv5a8imy">
              <label htmlFor={`identite-${field.id}`} className={labelClass} data-id="element-2853" data-mp="2ihy4t4py5yj6">
                {field.label}
              </label>
              <input id={`identite-${field.id}`} type={field.type} defaultValue={field.value} className={fieldClass} data-id="element-2854" data-mp="2mirt3z7axfcl" />
            </div>)}
      </div>

    </section>;
}

/** Date de naissance affichée en clair une fois verrouillée. */
function formatValue(id: string, value: string): string {
  if (id !== 'naissance' || value === '') return value;
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
}